<template>
  <ModuleWrapper :moduleId="moduleId">
    <template #main>
      <StudioSpinner v-if="isRetrieveVanityEmailDomainsLoading" />
      <div v-else class="tw-border tw-p-4">
        <div class="tw-mb-4">
          <p class="tw-text-sm">
            Copy the below values and paste them for the respective record types at your domain provider’s site.
          </p>
          <p class="tw-mt-4 tw-text-sm tw-font-semibold">
            Once the records are added, click on Verify CTA after waiting for a few minutes or time indicated by the
            record’s TTL (time-to-live) setting.
          </p>
        </div>
        <div class="tw-mb-4 tw-rounded-md tw-border">
          <div class="tw-flex tw-border-b tw-px-4 tw-py-2">
            <p class="tw-font-mono tw-text-sm tw-text-slate-600">TXT</p>
          </div>
          <div class="tw-p-4">
            <StudioInput
              id="dkim-host-read-only-input"
              label="Host (Name)"
              class="tw-mb-4"
              :modelValue="getRecordValue?.txtRecord?.name"
              showCopyToClipboard
              disabled
            />
            <StudioInput
              id="dkim-value-read-only-input"
              label="Value"
              :modelValue="getRecordValue?.txtRecord?.value"
              showCopyToClipboard
              disabled
            />
          </div>
        </div>
        <div class="tw-mb-4 tw-rounded-md tw-border">
          <div class="tw-flex tw-border-b tw-px-4 tw-py-2">
            <p class="tw-font-mono tw-text-sm tw-text-slate-600">CNAME</p>
          </div>
          <div v-for="record in getRecordValue?.cnameRecords" :key="record.value" class="tw-mt-2 tw-p-4">
            <StudioInput
              id="return-path-host-read-only-input"
              label="Host (Name)"
              class="tw-mb-4"
              :modelValue="record.name"
              showCopyToClipboard
              disabled
            />
            <StudioInput
              id="return-path-value-read-only-input"
              label="Value"
              :modelValue="record.value"
              showCopyToClipboard
              disabled
            />
          </div>
        </div>
        <div>
          <div v-if="vanityEmailDomain?.email_domain_verified" class="tw-mb-4 tw-flex tw-justify-end">
            <StudioBadge variation="green"> Record Verified </StudioBadge>
          </div>
          <div v-else-if="isVerificationPending" class="tw-mb-4 tw-flex tw-justify-end">
            <StudioBadge variation="red"> Verification Pending </StudioBadge>
          </div>
        </div>
        <div class="tw-mb-4 tw-flex tw-gap-4 tw-rounded-md tw-border tw-p-4">
          <div class="tw-h-6 tw-w-6">
            <ExclamationTriangle class="tw-h-6 tw-w-6 tw-text-yellow-500" />
          </div>
          <p class="tw-text-xs">
            DNS changes may take upto 72 hours to update depending on your host. If you have difficulties with this
            process, please reach out to your IT department for help. Until the changes are verified, you will see
            verification pending status for the email added and Goldcast default will be used for email notifications.
          </p>
        </div>
        <div v-if="isVerifyButtonClicked" class="tw-mb-4 tw-flex tw-gap-4 tw-rounded-md tw-border tw-p-4">
          <div class="tw-h-6 tw-w-6">
            <ExclamationTriangle class="tw-h-6 tw-w-6 tw-text-yellow-500" />
          </div>
          <p class="tw-text-xs">
            DNS changes are either missing or still processing. Please add the records if you haven’t already. If you
            have already added the records, ensure the records are added with values specified above and wait for few
            minutes before clicking on Verify again.
          </p>
        </div>
      </div>
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { VanityEmailDomainResponse, DomainVerification } from '@goldcast/api/backend';
import { defineComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';

import ModuleWrapper from '../ModuleWrapper.vue';

import ExclamationTriangle from '@/studioComponents/Icons/ExclamationTriangle.vue';
import StudioBadge from '@/studioComponents/StudioBadge.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';

export default defineComponent({
  name: 'VerifyRecords',
  components: {
    ModuleWrapper,
    StudioInput,
    StudioBadge,
    ExclamationTriangle,
    StudioSpinner
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  computed: {
    ...(mapState('customDomain', {
      isRetrieveVanityEmailDomainsLoading: 'isRetrieveVanityEmailDomainsLoading',
      isVerifyButtonClicked: 'isVerifyButtonClicked',
      vanityEmailDomain: 'vanityEmailDomain',
      isVerificationPending: 'isVerificationPending'
    }) as {
      isRetrieveVanityEmailDomainsLoading: () => boolean;
      isVerifyButtonClicked: () => boolean;
      vanityEmailDomain: () => VanityEmailDomainResponse;
      isVerificationPending: () => boolean;
    }),
    ...mapGetters('org', {
      org: 'getCurrentOrg'
    }),
    getRecordValue(): { txtRecord: DomainVerification | undefined; cnameRecords: DomainVerification[] } | undefined {
      if (!this.vanityEmailDomain) {
        return {
          domainVerification: undefined,
          dkimRecords: []
        } as any;
      }

      let txtRecord: DomainVerification | undefined = undefined;
      let cnameRecords: DomainVerification[] = [];

      if (this.vanityEmailDomain?.provider === 'POSTMARK') {
        txtRecord = {
          type: 'TXT',
          name: this.vanityEmailDomain.dkim_host,
          value: this.vanityEmailDomain.dkim_text_value
        };
        cnameRecords = [
          {
            type: 'CNAME',
            name: this.vanityEmailDomain.return_path_host,
            value: this.vanityEmailDomain.return_path_value
          }
        ];

        return {
          txtRecord: txtRecord,
          cnameRecords: cnameRecords
        };
      }

      return {
        txtRecord: this.vanityEmailDomain.domain_verification ?? undefined,
        cnameRecords: this.vanityEmailDomain?.dkim_records ?? []
      };
    }
  }
});
</script>
